import React, { useState, useEffect, ReactNode } from 'react';
import { authService } from './authService';
import { AuthContext } from './AuthContext';
import { AuthUser } from './types';
import { Organization } from '../../types/shared';
import { ApiClient } from '../../utils/apiClient';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchOrganization = async () => {
    try {
      const response = await ApiClient.get('/organizations/current/');
      if (response.ok) {
        const orgData = await response.json();
        setOrganization(orgData);
      }
    } catch {
      // Silently fail if organization fetch fails
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = authService.getAccessToken();
        if (token) {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          await fetchOrganization();
        }
      } catch (error) {
        authService.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = (user: AuthUser, accessToken: string, refreshToken: string) => {
    authService.setTokens(accessToken, refreshToken);
    setUser(user);
  };

  const logout = () => {
    authService.clearTokens();
    setUser(null);
  };

  const refreshOrganization = async () => {
    await fetchOrganization();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        refreshOrganization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
